import {
  useBillingBillingSettingsRetrieveQuery,
  useInvoicesPaymentLinkEmailCreateMutation,
} from '@bambi/api-client/bambi';
import type { InvoicePaymentLinkEmail } from '@bambi/api-client/schemas';
import { Button } from '@bambi/component-library/src/components/Button/Button';
import { CoreFormFieldEmail } from '@bambi/component-library/src/components/forms/fields/CoreFormFieldEmail';
import { CoreFormFieldText } from '@bambi/component-library/src/components/forms/fields/CoreFormFieldText';
import { CoreFormFieldTextArea } from '@bambi/component-library/src/components/forms/fields/CoreFormFieldTextArea';
import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import { EnvelopeIcon } from '@heroicons/react/24/outline';
import { useCallback, useEffect } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';

import { usePayerEmail } from '../../hooks/usePayerEmail';
import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import { show } from 'src/common/primitives/Toast/toast.slice';
import formatServerError from 'src/common/util/serverErrorFormatter';
import { selectCurrentOrganization } from 'src/features/auth/selectors';
import { paymentLinkEmailTemplateRenderer } from 'src/features/billing/settings/components/PaymentLinkTemplatePreview';
import { PAYMENT_LINK_EMAIL_TEMPLATE_DEFAULT } from 'src/features/billing/settings/constants';

interface EmailFormTabProps {
  invoiceNumber: string;
  paymentLinkId: string;
  paymentLinkUrl: string;
  hasInvoiceAttached: boolean;
  payerId: string;
  payerType: 'passenger' | 'payer_organization';
  onClose: () => void;
}

export function EmailFormTab({
  invoiceNumber,
  paymentLinkId,
  paymentLinkUrl,
  hasInvoiceAttached,
  payerId,
  payerType,
  onClose,
}: EmailFormTabProps) {
  const currentOrganization = useAppSelector(selectCurrentOrganization);
  const dispatch = useAppDispatch();
  const { isLoading, data: billing_data } =
    useBillingBillingSettingsRetrieveQuery({});
  const [createEmail, { isLoading: isCreatingEmail }] =
    useInvoicesPaymentLinkEmailCreateMutation();

  const form = useForm<InvoicePaymentLinkEmail>({
    reValidateMode: 'onBlur',
    defaultValues: {
      id: paymentLinkId,
      email_from: currentOrganization?.name ?? '',
      email_subject: `Payment link for invoice: ${invoiceNumber} from ${
        currentOrganization?.name ?? ''
      }`,
      include_invoice_as_attachment: hasInvoiceAttached,
    },
  });

  // Track updates to hasInvoiceAttached in form
  useEffect(() => {
    form.setValue('include_invoice_as_attachment', hasInvoiceAttached);
  }, [form, hasInvoiceAttached]);

  const { isLoading: isLoadingPayerEmail, email } = usePayerEmail(
    payerType,
    payerId
  );

  useEffect(() => {
    if (!isLoadingPayerEmail) {
      form.setValue('email_to', email ?? '');
    }
  }, [isLoadingPayerEmail, email, form]);

  // Just in case the form is stale for some reason
  // refresh the payment link id.
  useEffect(() => {
    form.setValue('id', paymentLinkId);
  }, [paymentLinkId, form]);

  const handleSubmit = useCallback<SubmitHandler<InvoicePaymentLinkEmail>>(
    async (data) => {
      try {
        const renderedBody = paymentLinkEmailTemplateRenderer(
          data.email_body,
          {
            billing_email: billing_data?.email ?? '',
            billing_phone: billing_data?.phone_number ?? '',
            invoice_number: invoiceNumber,
            organization_name: currentOrganization?.name ?? '',
            payment_link: paymentLinkUrl,
          },
          true
        );

        await createEmail({
          invoicePaymentLinkEmail: {
            ...data,
            email_body: renderedBody,
          },
        }).unwrap();

        dispatch(
          show({
            type: 'success',
            title: 'Successfully sent email',
          })
        );

        onClose();
      } catch (e) {
        dispatch(
          show({
            type: 'error',
            title: 'Failed to send email',
            description: formatServerError(e),
          })
        );
      }
    },
    [
      billing_data?.email,
      billing_data?.phone_number,
      createEmail,
      currentOrganization?.name,
      dispatch,
      invoiceNumber,
      onClose,
      paymentLinkUrl,
    ]
  );

  useEffect(() => {
    if (isLoading || !billing_data) {
      return;
    }

    const body =
      billing_data.payment_link_email_template ??
      PAYMENT_LINK_EMAIL_TEMPLATE_DEFAULT;
    form.setValue('email_body', body);
  }, [isLoading, billing_data, form]);

  return (
    <div className="flex flex-col gap-2">
      <Controller
        name="email_to"
        control={form.control}
        rules={{ required: 'This field is required' }}
        render={({ field, fieldState }) => (
          <CoreFormFieldEmail
            label="To *"
            inputProps={{
              ...field,
              id: 'email_to',
            }}
            helperText="Default value retrieved from payer. Use commas to separate multiple emails."
            error={fieldState.error?.message}
          />
        )}
      />
      <Controller
        name="email_subject"
        control={form.control}
        rules={{ required: 'This field is required' }}
        render={({ field, fieldState }) => (
          <CoreFormFieldText
            label="Subject *"
            inputProps={field}
            error={fieldState.error?.message}
          />
        )}
      />

      {isLoading ? (
        <div className="flex flex-row justify-center">
          <LoadingIndicator />
        </div>
      ) : (
        <Controller
          name="email_body"
          control={form.control}
          rules={{ required: 'This field is required' }}
          render={({ field, fieldState }) => (
            <CoreFormFieldTextArea
              label="Body *"
              inputProps={{
                ...field,
                rows: 6,
                id: 'email_body',
              }}
              error={fieldState.error?.message}
            />
          )}
        />
      )}
      <div className="flex flex-row justify-between">
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="primary"
          className="flex flex-row gap-2"
          loading={isCreatingEmail}
          onClick={form.handleSubmit(handleSubmit)}
        >
          <EnvelopeIcon className="h-[22px] w-[22px]" />
          Send Email
        </Button>
      </div>
    </div>
  );
}
